import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { boronClient } from "../../../../api";
import { createError, HTTPErrors } from "../../../../errors";
import { useQueryError } from "../../../../hooks/http/useQueryError";
import { GuardianAnnounceMessageTagInterface } from "../../../../interfaces/GuardianAnnounceMessageTagInterface";
import { useFetchMessageThreads } from "../useFetchMessageThreads";

type Props = {
  sectionId: string;
  initialOpenedTagId?: string;
};

export const useDestinationTag = ({ sectionId, initialOpenedTagId }: Props) => {
  const [openedTagId, setOpenedTagId] = useState<string | null>(
    initialOpenedTagId ?? null,
  );
  const { data, error } = useQuery<
    GuardianAnnounceMessageTagInterface[],
    HTTPErrors
  >({
    queryKey: ["guardian_announce_message_tags", sectionId],
    queryFn: async () => {
      const { response, data } = await boronClient.GET(
        "/api/v1/sections/{section_id}/guardian_announce_message_tags",
        { params: { path: { section_id: sectionId } } },
      );
      if (response.ok && data) {
        // `/guardian_announce_message_tags` は通常 `deleted` を返さないため、ドメイン型に合わせて
        // `false` を補完する（TAG_DISPLAY-01: `deleted` フィールドを返さないエンドポイントは `false` 扱い）。
        // 将来 Boron 側が `deleted` を返すようになった場合に備え、既に値があれば優先する（防御的）
        return data.guardianAnnounceMessageTags.map(
          (tag): GuardianAnnounceMessageTagInterface => ({
            ...tag,
            deleted: (tag as { deleted?: boolean }).deleted ?? false,
          }),
        );
      }
      throw await createError(response);
    },
  });
  useQueryError(error);

  // 過去送信履歴（スレッド一覧）には論理削除タグも含まれる（TAG_FETCH-02）。
  // `/guardian_announce_message_tags` には論理削除タグが含まれないため、スレッド一覧を
  // フォールバックの解決元として併用することで、URL から論理削除タグへ遷移した際にも
  // `openedThread` が解決され `deleted: true` が継続送信フォームに伝播する（TAG_HISTORY-01）。
  //
  // このフォールバック経路は論理削除タグ専用（`/guardian_announce_message_tags` に含まれない
  // タグ = 論理削除タグ）であり、継続送信フォームを disabled 化するためだけに使われる。
  // スレッドレスポンスには `enabled` / `studentCount` が含まれないため、それぞれ送信可能側 (`true`) と
  // 0 件の固定値で埋める：
  // - `enabled: true` — disabled 判定は `deleted` が優先されるため値の影響なし
  // - `studentCount: 0` — 論理削除タグは紐づき生徒数 0 が API 上も実態（既存のレシピエント取得は別途解決）
  const { data: threadsData } = useFetchMessageThreads({ sectionId });
  const threadDestinations = useMemo<GuardianAnnounceMessageTagInterface[]>(
    () =>
      threadsData?.pages.flatMap((page) =>
        page.data.map((thread) => ({
          id: thread.id,
          name: thread.name,
          type: thread.type,
          enabled: true,
          deleted: thread.deleted,
          studentCount: 0,
        })),
      ) ?? [],
    [threadsData],
  );

  const openedThread = useMemo(() => {
    if (!openedTagId) {
      return null;
    }
    const fromDestinationTags = data?.find((tag) => tag.id === openedTagId);
    if (fromDestinationTags) {
      return fromDestinationTags;
    }
    return threadDestinations.find((tag) => tag.id === openedTagId) ?? null;
  }, [data, threadDestinations, openedTagId]);

  const resetOpenedTagId = useCallback(() => {
    setOpenedTagId(null);
  }, [setOpenedTagId]);

  return {
    destinationTags: data,
    openedThread,
    setOpenedThreadId: setOpenedTagId,
    resetOpenedThreadId: resetOpenedTagId,
  };
};
